import { Component, OnInit, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { PosSettingsService } from '../../../../services/pos-settings.service';
import { POSSettingsModel, SalesTaxAndCommissionModel, MessageSettingsModel } from '../../../../Shared/Interfaces/IPOSSettings';
import { ANGULAR_MODULES, GENERAL_COMPONENTS, Operational_COMPONENTS, SYNCFUSION_MODULES } from 'src/app/Shared/shared-imports';
import { Subject, of, forkJoin } from 'rxjs';
import { takeUntil, map } from 'rxjs/operators';
import Swal from 'sweetalert2';
import { ToastrService } from 'ngx-toastr';
import '@angular/localize/init';
import { Router } from '@angular/router';
import { CurrencyPipe } from '@angular/common';
import { DecimalPipe } from '@angular/common';
import { VendorSearchComponent } from 'src/app/Shared/vendor-search/vendor-search.component';
import { formatDate } from '@angular/common';
import { WordReplacePipe } from 'src/app/Shared/word-replace.pipe';
import { SharedHTMLSettingsDataService } from 'src/app/Shared/sharedHTMLSettingsData.service';
import { IUserPermissionHandler } from 'src/app/Shared/model/CommonModel';
import { UserPermissionHandlerDirective } from 'src/app/Shared/userPermissionHandler.directive';
import { EActionTypes, EUserSecurityFunctionID } from 'src/app/Shared/enum';
import { TooltipModule } from "@syncfusion/ej2-angular-popups";
import { POSCustomerSearchComponent } from 'src/app/Shared/pos-customer-search/pos-customer-search.component';
import * as bootstrap from 'bootstrap';
import { VendorService } from 'src/app/services/Vendor.service';
import { PosSalesService } from 'src/app/services/possalesservice';
import { PercentageFormatterDirective } from 'src/app/Shared/percentageFormatter.directive';
import { CurrencyFormatterDirective } from 'src/app/Shared/currencyFormatter.directive';
import { LayoutService } from 'src/app/services/layout.service';
import { InventoryService } from 'src/app/services/inventory.service';
import { UserService } from 'src/app/services/User.service';
import { GeneralService } from 'src/app/services/general.service';
import { IUserPermissionsModel } from 'src/app/Shared/model/AdminUserModel';
import { LoaderComponent } from 'src/app/Shared/loader/loader.component';

@Component({
  selector: 'app-ops-pos-settings',
  standalone: true,
  imports: [Operational_COMPONENTS, ANGULAR_MODULES, GENERAL_COMPONENTS, SYNCFUSION_MODULES, VendorSearchComponent, WordReplacePipe, UserPermissionHandlerDirective, TooltipModule, POSCustomerSearchComponent, PercentageFormatterDirective, CurrencyFormatterDirective, LoaderComponent],
  templateUrl: './ops-pos-settings.html',
  styleUrl: './ops-pos-settings.scss',
  providers: [CurrencyPipe, DecimalPipe]
})

export class OpsPosSettings implements OnInit, OnDestroy {
  isCollapsed = true;
  public loaderIcon: Promise<boolean>;

  VendorFilterOption = {
    All: true,
    Current: false,
    Inactive: false,
    PageModel: {
      pageCount: 1,
      pageSize: 20
    }
  };

  posSettingsForm!: FormGroup;
  taxSettingsForm!: FormGroup;
  messageSettingsForm!: FormGroup;
  originalPosSettings: any;
  originalTaxSettings: any;
  originalMessageSettings: any;
  private destroy$ = new Subject<void>();

  today: Date = new Date();
  minExpiryDate: Date = new Date();
  activeCustomerField: 'retail' | 'pending' | null = null;
  private modalInstance: bootstrap.Modal | null = null;
  barcodeTemplates: any[] = [];
  selectedVendorContext: 'storeDiscount' | 'giftCertificate' | null = null;

  viewPermissionHandlerData: IUserPermissionHandler = { FunctionId: EUserSecurityFunctionID.POSSettings, Action: EActionTypes.VIEW };
  editPermissionHandlerData: IUserPermissionHandler = { FunctionId: EUserSecurityFunctionID.POSSettings, Action: EActionTypes.EDIT };
  allowRetail = false;
  allowWholeSale = false;
  allowShipped = false;
  allowLayaway = false;
  allowPending = false;
  allowReturn = false;
  allowVoid = false;
  @ViewChild('customerModal', { static: false }) customerModal: ElementRef;

  constructor(
    private fb: FormBuilder,
    private posSettingsService: PosSettingsService,
    private toastr: ToastrService,
    private router: Router,
    private currencyPipe: CurrencyPipe,
    private userService: UserService,
    private decimalPipe: DecimalPipe,
    private sharedHTMLSettings: SharedHTMLSettingsDataService,
    private vendorService: VendorService,
    private wordReplacePipe: WordReplacePipe,
    private posSalesService: PosSalesService,
    private layoutService: LayoutService,
    private generalService: GeneralService,
    private inventoryService: InventoryService
  ) { }

  ngOnInit(): void {
    this.initForms();

    this.setupToggleControls(this.posSettingsForm.get('OtherSettings') as FormGroup, 'UseBarcodeScanner', ['UseNoScannerDefault']);

    const otherSettings = this.posSettingsForm.get('OtherSettings') as FormGroup;

    const barcodeCtrl = otherSettings.get('UseBarcodeScanner');
    const noScannerCtrl = otherSettings.get('UseNoScannerDefault');

    barcodeCtrl?.valueChanges.subscribe((isChecked: boolean) => {
      if (!isChecked) {
        noScannerCtrl?.setValue(false, { emitEvent: false });
      }
    });

    if (!barcodeCtrl?.value) {
      noScannerCtrl?.setValue(false, { emitEvent: false });
    }

    const discountGroup = this.posSettingsForm.get('DiscountSettings') as FormGroup;

    this.setupToggleControls(discountGroup, 'EnableDiscountWarnings', [
      'Warnon100PercentDiscount',
      'WarnonAnyDiscount'
    ]);

    this.setupToggleControls(discountGroup, 'EnableUniversalDiscount', [
      'Udfromdate',
      'UdfromTime',
      'Udtodate',
      'UdtoTime',
      'Udtype',
      'Udvalue',
      'ApplyVenDiscountaboveUd'
    ]);

    this.setupToggleControls(discountGroup, 'EnableStoreDiscount', [
      'MarketVendorId',
      'StoreDicountType',
      'StoreDiscountValue'
    ]);

    this.setupToggleControls(
      this.posSettingsForm.get('SalesTypesSettings') as FormGroup,
      'AllowLayaway',
      [
        'LayawaySettings.DownPaymentPercent',
        'LayawaySettings.LayawayNumberOfDays',
        'LayawaySettings.NumberOfInstalments',
        'LayawaySettings.LayawayMessage',
        'LayawaySettings.InclLwayInPayOut'
      ]
    );

    this.setupTaxToggleMappings();

    // Two fields → one field subscriptions
    this.taxSettingsForm.get('TaxSettings.ApplyPossalesTax')?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(() => this.updateApplyOnPossalesTax());
    this.taxSettingsForm.get('TaxSettings.ApplyPossecondLevelTax')?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(() => this.updateApplyOnPossalesTax());

    this.updateApplyOnPossalesTax();
    this.setupDefaultSaleTypeLogic();

    this.setupTypeWatcher(
      'DiscountSettings.Udtype',
      'DiscountSettings.Udvalue',
      'input[formcontrolname="Udvalue"]'
    );

    this.setupTypeWatcher(
      'DiscountSettings.StoreDicountType',
      'DiscountSettings.StoreDiscountValue',
      'input[formcontrolname="StoreDiscountValue"]'
    );

    this.posSettingsForm.get('DiscountSettings.Udfromdate')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((start: Date) => {
        if (start) {
          this.minExpiryDate = new Date(start);
          const expiry = this.posSettingsForm.get('DiscountSettings.Udtodate')?.value;

          if (expiry && new Date(expiry) < new Date(start)) {
            this.posSettingsForm.get('DiscountSettings.Udtodate')?.setValue(null);
          }
        }
      });

    this.setupConditionalValidators(this.posSettingsForm, 'DiscountSettings.EnableUniversalDiscount', [
      { controlPath: 'DiscountSettings.Udfromdate', validators: [Validators.required] },
      { controlPath: 'DiscountSettings.Udtodate', validators: [Validators.required] },
      { controlPath: 'DiscountSettings.UdfromTime', validators: [Validators.required] },
      { controlPath: 'DiscountSettings.UdtoTime', validators: [Validators.required] },
      { controlPath: 'DiscountSettings.Udtype', validators: [Validators.required] },
      { controlPath: 'DiscountSettings.Udvalue', validators: [Validators.required, Validators.min(0.0001)] },
    ]);

    this.setupConditionalValidators(this.posSettingsForm, 'DiscountSettings.EnableStoreDiscount', [
      { controlPath: 'DiscountSettings.MarketVendorId', validators: [Validators.required] },
      { controlPath: 'DiscountSettings.StoreDicountType', validators: [Validators.required] },
      { controlPath: 'DiscountSettings.StoreDiscountValue', validators: [Validators.required, Validators.min(0.0001)] },
    ]);

    this.setupConditionalValidators(this.posSettingsForm, 'SalesTypesSettings.AllowLayaway', [
      { controlPath: 'LayawaySettings.DownPaymentPercent', validators: [Validators.required, Validators.min(0), Validators.max(100)] },
      { controlPath: 'LayawaySettings.LayawayNumberOfDays', validators: [Validators.required, Validators.min(0)] },
      { controlPath: 'LayawaySettings.NumberOfInstalments', validators: [Validators.required, Validators.min(0)] },
    ]);

    this.setupConditionalValidators(this.taxSettingsForm, 'TaxSettings.ApplyPossalesTax', [
      { controlPath: 'TaxSettings.PossalesTaxPercent', validators: [Validators.required, Validators.min(0.0001), Validators.max(100)] },
    ]);

    this.setupConditionalValidators(this.taxSettingsForm, 'TaxSettings.ApplyPossecondLevelTax', [
      { controlPath: 'TaxSettings.PossecondLevelTaxPercent', validators: [Validators.required, Validators.min(0.0001), Validators.max(100)] },
      { controlPath: 'TaxSettings.PossecondLevelTaxName', validators: [Validators.required, Validators.maxLength(50)] },
    ]);

    this.setupConditionalValidators(this.taxSettingsForm, 'Commission.ApplyCommission', [
      { controlPath: 'Commission.DealerConCommPercent', validators: [Validators.required, Validators.min(0.0001), Validators.max(100)] },
    ]);

    this.setupConditionalValidators(this.taxSettingsForm, 'POSSales.FillDefaultIteminPos', [
      { controlPath: 'POSSales.DefaultItemName', validators: [Validators.required] },
    ]);

    this.setupConditionalValidators(this.taxSettingsForm, 'POSSales.EnableOtherChargeinPos', [
      { controlPath: 'POSSales.PosotherChargeName', validators: [Validators.required] },
    ]);

    this.loadBarcodeTemplateSettings();
    this.loadSettings();
    this.setupPosByVendorValidation();
  }

  private initForms(): void {
    this.posSettingsForm = this.fb.group({
      SalesTypesSettings: this.fb.group({
        AllowRetail: [false],
        AllowWholeSale: [false],
        AllowShipped: [false],
        AllowLayaway: [false],
        AllowPending: [false],
        AllowReturn: [false],
        AllowVoid: [false],
        DefaultSaleType: [null, [Validators.required]],
        DefCustIdForRetail: [null],
        MarketCustIdForRetail: [null],
        CustIdReqPendingSale: [false],
        DefCustIdForPendingSale: [null],
        MarketCustIdForPendingSale: [null]
      }),
      OtherSettings: this.fb.group({
        TrackSalesbyPerson: [false],
        PosbyVendor: [false],
        AllowAutoConsignmentId: [false],
        UserwiseDayend: [false],
        UsePoleDisplay: [false],
        IssueGc: [false],
        TouchScreenPos: [false],
        QbmappingNew: [false],
        UseQboforCheckPrinting: [false],
        AllowVendorDiscount: [false],
        NegativePosPayoutAmtAdjust: [false],
        UseBarcodeScanner: [false],
        UseNoScannerDefault: [false],
        DefaultBarcodeTemplate: [null, Validators.maxLength(30)],
        AutomaticInventoryStockID: [false],
        TravelerCheckTerm: ['', Validators.maxLength(50)],
        MakeTravelerNoOptional: [false],
        DeletePendingCustomer: [false],
        UserSalesToolbar: [false],
        OpeningCashAmount: [null],
        GcmaxDays: [null],
        ChgPayModeInVoid: [false],
        GcSaleTerm: ['', Validators.maxLength(10)],
        GcVendorId: [null],
        GcMarketVendorId: [null],
        GcBoothNumber: ['', Validators.maxLength(10)],
        IsPOSCashRounding: [false],
        ExcludeOpenCashAmtFromDayEnd: [false]
      }),
      DiscountSettings: this.fb.group({
        EnableDiscountWarnings: [false],
        Warnon100PercentDiscount: [false],
        WarnonAnyDiscount: [false],
        EnableUniversalDiscount: [false],
        Udfromdate: [null],
        UdfromTime: [null],
        Udtodate: [null],
        UdtoTime: [null],
        Udtype: [null],
        Udvalue: [null],
        ApplyVenDiscountaboveUd: [false],
        EnableStoreDiscount: [false],
        StoreDiscountVendorId: [null],
        MarketVendorId: [null],
        StoreDicountType: [null],
        StoreDiscountValue: [null]
      }),
      LayawaySettings: this.fb.group({
        DownPaymentPercent: [0],
        LayawayNumberOfDays: [0],
        NumberOfInstalments: [0],
        LayawayMessage: ['', Validators.maxLength(500)],
        InclLwayInPayOut: [false]
      })
    });

    this.taxSettingsForm = this.fb.group({
      TaxSettings: this.fb.group({
        ApplyPostaxItemwise: [false],
        ApplyPossalesTax: [false],
        PossalesTaxPercent: [null],
        ApplyOnPossalesTax: [false],
        ApplyPossecondLevelTax: [false],
        PossecondLevelTaxPercent: [null],
        PossecondLevelTaxName: ['', Validators.maxLength(50)]
      }),
      Commission: this.fb.group({
        FlatCommission: [null, [Validators.min(0), Validators.max(100)]],
        ApplyCommission: [false],
        DealerConCommPercent: [null],
        AllowDropppingCommission: [false],
        ShowCommissiononPos: [false],
        AllowCommissionOverride: [false]
      }),
      POSSales: this.fb.group({
        AllowPossalesWithOutBooking: [false],
        FillDefaultIteminPos: [false],
        DefaultItemName: ['', Validators.maxLength(100)],
        ConfirmDiscountinPos: [false],
        EnableOtherChargeinPos: [false],
        PosotherChargeName: ['', Validators.maxLength(50)]
      })
    });

    this.messageSettingsForm = this.fb.group({
      ReturnPolicy: ['', Validators.maxLength(250)],
      PrintReturnPolicyonReceipt: [false],
      WorkingHours: ['', Validators.maxLength(250)],
      PrintWorkingHoursonReceipt: [false],
      Advertisement: ['', Validators.maxLength(250)],
      AdvertisementonReceipt: [false],
      Comments: ['', Validators.maxLength(250)],
      CommentsonReceipt: [false],
      UseFbinPos: [false],
      UseTwinPos: [false],
      SettlementMessage: ['', Validators.maxLength(250)],
      PrintSettlementMessage: [false]
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  setVendorContext(context: 'storeDiscount' | 'giftCertificate') {
    this.selectedVendorContext = context;
  }

  getFormatterType(typePath: string): 'currency' | 'percentage' | 'none' {
    const control = this.posSettingsForm.get(typePath);
    if (!control || control.value == null) return 'none';

    return control.value == 1 ? 'currency' : 'percentage';
  }

  openCustomerModal(field: 'retail' | 'pending') {
    this.activeCustomerField = field;
    const modalEl = this.customerModal.nativeElement;
    this.modalInstance = bootstrap.Modal.getOrCreateInstance(modalEl);
    this.modalInstance.show();
  }

  CustomerInfo(data: any) {
    const customerId = data.CustomerId;
    const marketCustomerId = data.MarketCustomerId;

    if (this.activeCustomerField === 'retail') {
      this.posSettingsForm.get('SalesTypesSettings.DefCustIdForRetail')
        ?.setValue(customerId);
      this.posSettingsForm.get('SalesTypesSettings.MarketCustIdForRetail')
        ?.setValue(marketCustomerId);
    } else if (this.activeCustomerField === 'pending') {
      this.posSettingsForm.get('SalesTypesSettings.DefCustIdForPendingSale')
        ?.setValue(customerId);
      this.posSettingsForm.get('SalesTypesSettings.MarketCustIdForPendingSale')
        ?.setValue(marketCustomerId);
    }

    this.closeCustomerInfoModal();
  }

  closeCustomerInfoModal() {
    if (this.modalInstance) {
      this.modalInstance.hide();
      this.modalInstance = null;
    }
  }

  private populateMarketCustomerId(customerId: number, saleType: 'retail' | 'pending') {
    if (customerId === null || customerId === undefined) return;

    this.posSalesService.getCustomerDetailsById(customerId)
      .pipe(takeUntil(this.destroy$))
      .subscribe(customer => {
        const marketCustomerId = customer?.MarketCustomerId;

        if (marketCustomerId !== null && marketCustomerId !== undefined) {
          if (saleType === 'retail') {
            this.posSettingsForm.get('SalesTypesSettings.MarketCustIdForRetail')
              ?.setValue(marketCustomerId, { emitEvent: false });
          } else {
            this.posSettingsForm.get('SalesTypesSettings.MarketCustIdForPendingSale')
              ?.setValue(marketCustomerId, { emitEvent: false });
          }
        } else {
          if (saleType === 'retail') {
            this.posSettingsForm.get('SalesTypesSettings.MarketCustIdForRetail')?.reset({ emitEvent: false });
          } else {
            this.posSettingsForm.get('SalesTypesSettings.MarketCustIdForPendingSale')?.reset({ emitEvent: false });
          }
        }
      });
  }

  loadBarcodeTemplateSettings(): void {
    this.inventoryService.getBarcodeTemplateSettings()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.barcodeTemplates = res;
        },
        error: (err) => {
          this.toastr.error($localize`Error fetching barcode template settings.`, $localize`Error`, this.sharedHTMLSettings.ToasterConfig);
        }
      });
  }

  private setupToggleControls(
    parentGroup: FormGroup,
    toggleControlName: string,
    dependentControlPaths: string[]
  ) {
    const toggleCtrl = parentGroup.get(toggleControlName);

    if (!toggleCtrl) return;

    toggleCtrl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((isChecked: boolean) => {
      dependentControlPaths.forEach(path => {
        let control = parentGroup.get(path);

        if (!control) {
          control = this.posSettingsForm.get(path);
        }

        if (control) {
          if (isChecked) {
            control.enable({ emitEvent: false });
          } else {
            control.disable({ emitEvent: false });
            control.reset();
          }
        }
      });
    });

    if (!toggleCtrl.value) {
      dependentControlPaths.forEach(path => {
        let control = parentGroup.get(path) || this.posSettingsForm.get(path);
        control?.disable({ emitEvent: false });
      });
    }
  }

  private setupTaxToggleMappings(): void {
    const toggleMappings: { checkbox: string; targets: string[] }[] = [
      {
        checkbox: 'TaxSettings.ApplyPossalesTax',
        targets: ['TaxSettings.PossalesTaxPercent']
      },
      {
        checkbox: 'TaxSettings.ApplyPossecondLevelTax',
        targets: [
          'TaxSettings.PossecondLevelTaxPercent',
          'TaxSettings.PossecondLevelTaxName'
        ]
      },
      {
        checkbox: 'POSSales.FillDefaultIteminPos',
        targets: ['POSSales.DefaultItemName']
      },
      {
        checkbox: 'POSSales.EnableOtherChargeinPos',
        targets: ['POSSales.PosotherChargeName']
      },
      {
        checkbox: 'Commission.ApplyCommission',
        targets: ['Commission.DealerConCommPercent']
      },
      {
        checkbox: 'Commission.ShowCommissiononPos',
        targets: ['Commission.AllowCommissionOverride']
      }
    ];

    toggleMappings.forEach(mapping => {
      const controller = this.taxSettingsForm.get(mapping.checkbox);

      if (controller) {
        controller.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((isChecked: boolean) => {
          mapping.targets.forEach((targetName, index) => {
            const targetControl = this.taxSettingsForm.get(targetName);
            if (!targetControl) return;

            if (isChecked) {
              targetControl.enable();

              // Auto-focus only the FIRST target field for that checkbox
              if (index === 0) {
                setTimeout(() => {
                  const controlName = targetName.split('.').pop();
                  const input = document.querySelector<HTMLInputElement>(
                    `input[formcontrolname="${controlName}"]`
                  );
                  input?.focus();
                  // input?.select(); // optional: select existing text for instant editing
                }, 100);
              }

            } else {
              targetControl.reset();
              targetControl.disable();
            }
          });

          this.updateApplyOnPossalesTax();
        });

        mapping.targets.forEach(targetName => {
          const targetControl = this.taxSettingsForm.get(targetName);
          if (targetControl) {
            if (controller.value) {
              targetControl.enable();
            } else {
              targetControl.reset();
              targetControl.disable();
            }
          }
        });
      }
    });
  }

  private updateApplyOnPossalesTax(): void {
    const first = this.taxSettingsForm.get('TaxSettings.ApplyPossalesTax')?.value;
    const second = this.taxSettingsForm.get('TaxSettings.ApplyPossecondLevelTax')?.value;
    const target = this.taxSettingsForm.get('TaxSettings.ApplyOnPossalesTax');

    if (target) {
      if (first && second) {
        target.enable()
      }
      else {
        target.reset();
        target.disable();
      }
    }
  }

  onStartDateChange(event: any): void {
    const startDate = event.value;
    if (startDate) {
      this.minExpiryDate = new Date(startDate);
    }
  }

  private setupTypeWatcher(typePath: string, valuePath: string, selector: string) {
    const typeCtrl = this.posSettingsForm.get(typePath);
    const valueCtrl = this.posSettingsForm.get(valuePath);

    if (!typeCtrl || !valueCtrl) return;

    let previousType = typeCtrl.value;
    const input = document.querySelector<HTMLInputElement>(selector);

    if (!previousType) {
      valueCtrl.disable({ emitEvent: false });
      if (input) input.setAttribute('disabled', 'true');
    }

    typeCtrl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((newType: number) => {
      if (newType !== previousType) {
        valueCtrl.setValue(0.00, { emitEvent: false });

        if (input) {
          if (newType === 1) {
            input.value = this.currencyPipe.transform(0, '$', 'symbol', '1.2-2') || '$0.00';
          } else if (newType === 2) {
            input.value = (this.decimalPipe.transform(0, '1.2-4') || '0.0000') + '%';
          } else {
            input.value = '';
          }
        }

        previousType = newType;
      }

      if (newType) {
        valueCtrl.enable({ emitEvent: false });
        if (input) input.removeAttribute('disabled');
      } else {
        valueCtrl.disable({ emitEvent: false });
        if (input) input.setAttribute('disabled', 'true');
      }
    });
  }

  private setupDefaultSaleTypeLogic(): void {
    const salesSettings = this.posSettingsForm.get('SalesTypesSettings') as FormGroup;

    salesSettings.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(values => {
      const { AllowRetail, AllowWholeSale, AllowShipped, AllowLayaway, AllowPending, DefaultSaleType } = values;

      const firstFiveUnchecked = !AllowRetail && !AllowWholeSale && !AllowShipped && !AllowLayaway && !AllowPending;

      // If all first 5 are unchecked → set Retail as checked and default
      if (firstFiveUnchecked) {
        salesSettings.patchValue({
          AllowRetail: true,
          DefaultSaleType: 1
        }, { emitEvent: false });
      }

      // Prevent unchecking Retail if it's the only one checked
      const onlyRetailChecked = AllowRetail && !AllowWholeSale && !AllowShipped && !AllowLayaway && !AllowPending;
      if (onlyRetailChecked && !DefaultSaleType) {
        salesSettings.patchValue({ DefaultSaleType: 1 }, { emitEvent: false });
      }
    });
  }

  onVendorSelected(vendor: any) {
    if (this.selectedVendorContext === 'storeDiscount') {
      this.posSettingsForm.patchValue({
        DiscountSettings: {
          StoreDiscountVendorId: vendor.vendorId,
          MarketVendorId: vendor.marketVendorId
        }
      });
    } else if (this.selectedVendorContext === 'giftCertificate') {
      this.posSettingsForm.patchValue({
        OtherSettings: {
          GcVendorId: vendor.vendorId,
          GcMarketVendorId: vendor.marketVendorId
        }
      });
    }

    // Close focus from search button
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }

    // Reset context
    this.selectedVendorContext = null;
  }

  onDiscountRadioChange(type: '100' | 'any') {
    if (!this.posSettingsForm.get('DiscountSettings.EnableDiscountWarnings')?.value) {
      return;
    }

    if (type === '100') {
      this.posSettingsForm.patchValue({
        DiscountSettings: {
          Warnon100PercentDiscount: true,
          WarnonAnyDiscount: false
        }
      });
    } else {
      this.posSettingsForm.patchValue({
        DiscountSettings: {
          Warnon100PercentDiscount: false,
          WarnonAnyDiscount: true
        }
      });
    }
  }

  allowOnlyDigits(event: Event, maxDigits: number, controlPath: string) {
    const input = event.target as HTMLInputElement;
    let value = input.value.replace(/\D/g, '');
    if (maxDigits && value.length > maxDigits) {
      value = value.substring(0, maxDigits);
    }
    input.value = value;

    if (controlPath) {
      this.posSettingsForm.get(controlPath)?.setValue(value ? Number(value) : null, { emitEvent: true });
    }
  }

  allowOnlyAlphanumeric(event: KeyboardEvent) {
    const allowedKeys = [
      'Backspace', 'Tab', 'ArrowLeft', 'ArrowRight', 'Delete', 'Enter'
    ];

    if (allowedKeys.includes(event.key)) {
      return; // allow control keys
    }

    // Only allow letters (a-z, A-Z) and digits (0-9)
    const regex = /^[a-zA-Z0-9]$/;
    if (!regex.test(event.key)) {
      event.preventDefault();
      return;
    }
  }

  private loadSettings(): void {
    this.posSettingsService.getPosSettings()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data: POSSettingsModel) => {
          // Convert only time fields into Date objects if they exist
          const layaway = data.LayawaySettings;

          this.posSettingsForm.patchValue({
            ...data,
            DiscountSettings: {
              ...data.DiscountSettings,
              UdfromTime: data.DiscountSettings?.UdfromTime
                ? new Date(`1970-01-01T${data.DiscountSettings.UdfromTime}`)
                : null,
              UdtoTime: data.DiscountSettings?.UdtoTime
                ? new Date(`1970-01-01T${data.DiscountSettings.UdtoTime}`)
                : null
            },
            LayawaySettings: {
              ...layaway,
              DownPaymentPercent: layaway?.DownPaymentPercent ?? 0,
              LayawayNumberOfDays: layaway?.LayawayNumberOfDays ?? 0,
              NumberOfInstalments: layaway?.NumberOfInstalments ?? 0
            }
          });

          const customerIdForRetail = data.SalesTypesSettings?.DefCustIdForRetail;
          if (customerIdForRetail !== null && customerIdForRetail !== undefined) {
            this.populateMarketCustomerId(customerIdForRetail, 'retail');
          }

          const customerIdForPendingSale = data.SalesTypesSettings?.DefCustIdForPendingSale;
          if (customerIdForPendingSale !== null && customerIdForPendingSale !== undefined) {
            this.populateMarketCustomerId(customerIdForPendingSale, 'pending');
          }

          const vendorIdForStoreDiscount = data.DiscountSettings?.StoreDiscountVendorId;
          if (vendorIdForStoreDiscount) {
            this.populateMarketVendorId(vendorIdForStoreDiscount, 'storeDiscount');
          }

          const vendorIdForGC = data.OtherSettings?.GcVendorId;
          if (vendorIdForGC) {
            this.populateMarketVendorId(vendorIdForGC, 'giftCertificate');
          }

          // Define fields needing special formatting after load
          const formatFields = [
            {
              controlPath: 'OtherSettings.OpeningCashAmount',
              selector: 'input[formcontrolname="OpeningCashAmount"]',
              type: 'currency'
            },
            {
              controlPath: 'LayawaySettings.DownPaymentPercent',
              selector: 'input[formcontrolname="DownPaymentPercent"]',
              type: 'percentage'
            },
            {
              controlPath: 'DiscountSettings.Udvalue',
              selector: 'input[formcontrolname="Udvalue"]',
              type: this.posSettingsForm.get('DiscountSettings.Udtype')?.value === 1 ? 'currency' : 'percentage'
            },
            {
              controlPath: 'DiscountSettings.StoreDiscountValue',
              selector: 'input[formcontrolname="StoreDiscountValue"]',
              type: this.posSettingsForm.get('DiscountSettings.StoreDicountType')?.value === 1 ? 'currency' : 'percentage'
            }
          ];

          setTimeout(() => {
            formatFields.forEach(field => {
              const value = this.posSettingsForm.get(field.controlPath)?.value || 0;
              const el = document.querySelector<HTMLInputElement>(field.selector);

              if (el) {
                if (field.type === 'currency') {
                  el.value =
                    this.currencyPipe.transform(value, '$', 'symbol', '1.2-2') || '$0.00';
                } else if (field.type === 'percentage') {
                  el.value =
                    (this.decimalPipe.transform(value, '1.2-4') || '0.0000') + '%';
                }
              }
            });
          });

          this.originalPosSettings = { ...this.posSettingsForm.value };
          this.posSettingsForm.markAsPristine();
        },
        error: err => {
          this.toastr.error($localize`Failed to load POS Settings.`, $localize`Error`, this.sharedHTMLSettings.ToasterConfig);
        }
      });

    this.posSettingsService.getSalesTaxAndCommission()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data: SalesTaxAndCommissionModel) => {
          this.taxSettingsForm.patchValue(data);

          // Define fields needing special formatting after load
          const formatFields = [
            {
              controlPath: 'TaxSettings.PossalesTaxPercent',
              selector: 'input[formcontrolname="PossalesTaxPercent"]',
              type: 'percentage'
            },
            {
              controlPath: 'TaxSettings.PossecondLevelTaxPercent',
              selector: 'input[formcontrolname="PossecondLevelTaxPercent"]',
              type: 'percentage'
            },
            {
              controlPath: 'Commission.FlatCommission',
              selector: 'input[formcontrolname="FlatCommission"]',
              type: 'percentage'
            },
            {
              controlPath: 'Commission.DealerConCommPercent',
              selector: 'input[formcontrolname="DealerConCommPercent"]',
              type: 'percentage'
            }
          ];

          setTimeout(() => {
            formatFields.forEach(field => {
              const value = this.taxSettingsForm.get(field.controlPath)?.value || 0;
              const el = document.querySelector<HTMLInputElement>(field.selector);

              if (el) {
                if (field.type === 'currency') {
                  el.value =
                    this.currencyPipe.transform(value, '$', 'symbol', '1.2-2') || '$0.00';
                } else if (field.type === 'percentage') {
                  el.value =
                    (this.decimalPipe.transform(value, '1.2-4') || '0.0000') + '%';
                }
              }
            });
          });

          this.originalTaxSettings = { ...this.taxSettingsForm.value };
          this.taxSettingsForm.markAsPristine();
        },
        error: err => {
          this.toastr.error($localize`Failed to load Sales Tax Settings.`, $localize`Error`, this.sharedHTMLSettings.ToasterConfig);
        }
      });

    this.posSettingsService.getMessageSettings()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data: MessageSettingsModel) => {
          this.messageSettingsForm.patchValue(data);
          this.originalMessageSettings = { ...this.messageSettingsForm.value };
          this.messageSettingsForm.markAsPristine();
        },
        error: err => {
          this.toastr.error($localize`Failed to load Message Settings.`, $localize`Error`, this.sharedHTMLSettings.ToasterConfig);
        }
      });
  }

  private populateMarketVendorId(vendorId: number, context: 'storeDiscount' | 'giftCertificate') {
    if (!vendorId) return;

    this.vendorService.GetVendorById(vendorId)
      .pipe(takeUntil(this.destroy$))
      .subscribe(vendor => {
        const marketVendorId = vendor?.MarketVendorId || null;

        if (context === 'storeDiscount') {
          this.posSettingsForm.get('DiscountSettings.MarketVendorId')
            ?.setValue(marketVendorId, { emitEvent: false });
        } else if (context === 'giftCertificate') {
          this.posSettingsForm.get('OtherSettings.GcMarketVendorId')
            ?.setValue(marketVendorId, { emitEvent: false });
        }
      });
  }

  private preparePayload(): POSSettingsModel {
    const raw = this.posSettingsForm.getRawValue();

    const formatIfValid = (value: any, fmt: string, fallback: any = null) => {
      if (!value) return fallback;
      const date = new Date(value);
      return isNaN(date.getTime()) ? fallback : formatDate(date, fmt, 'en-US');
    };

    const discount = raw.DiscountSettings;

    if (!discount.EnableUniversalDiscount) {
      discount.Udfromdate = null;
      discount.Udtodate = null;
      discount.UdfromTime = null;
      discount.UdtoTime = null;
      discount.Udtype = null;
      discount.Udvalue = null;
      discount.ApplyVenDiscountaboveUd = false;
    }

    if (!discount.EnableStoreDiscount) {
      discount.StoreDiscountVendorId = null;
      discount.MarketVendorId = null;
      discount.StoreDicountType = null;
      discount.StoreDiscountValue = null;
    }

    return {
      ...raw,
      DiscountSettings: {
        ...discount,
        Udfromdate: formatIfValid(discount.Udfromdate, 'yyyy-MM-dd', null),
        Udtodate: formatIfValid(discount.Udtodate, 'yyyy-MM-dd', null),
        UdfromTime: formatIfValid(discount.UdfromTime, 'HH:mm', null),
        UdtoTime: formatIfValid(discount.UdtoTime, 'HH:mm', null),
      }
    };
  }

  saveSettings(): void {
    const activeTabId = this.getActiveTabId();

    this.trimAllTextFields(this.posSettingsForm);
    this.trimAllTextFields(this.taxSettingsForm);
    this.trimAllTextFields(this.messageSettingsForm);

    Swal.fire({
      title: 'Confirm Save',
      text: 'Are you sure you want to save the changes?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Yes, Save it!',
      cancelButtonText: 'Cancel'
    }).then(result => {
      if (!result.isConfirmed) return;

      switch (activeTabId) {
        case 'Pos_Settings':
          const salesGroup = this.posSettingsForm.get('SalesTypesSettings') as FormGroup;
          const isCustIdMandatory = salesGroup.get('CustIdReqPendingSale')?.value;

          if (isCustIdMandatory) {
            this.posSettingsForm.get('SalesTypesSettings.DefCustIdForPendingSale')?.setValue(null, { emitEvent: false });
            this.posSettingsForm.get('SalesTypesSettings.MarketCustIdForPendingSale')?.setValue(null, { emitEvent: false });
          }

          const validUniversal = this.validateSection(
            this.posSettingsForm,
            'DiscountSettings.EnableUniversalDiscount',
            [
              { controlPath: 'DiscountSettings.Udfromdate', label: 'From Date' },
              { controlPath: 'DiscountSettings.Udtodate', label: 'To Date' },
              { controlPath: 'DiscountSettings.UdfromTime', label: 'From Time' },
              { controlPath: 'DiscountSettings.UdtoTime', label: 'To Time' },
              { controlPath: 'DiscountSettings.Udtype', label: 'Discount Type' },
              { controlPath: 'DiscountSettings.Udvalue', label: 'Discount Value' },
            ],
            'Universal Discount Settings',
            ['DiscountSettings']
          );
          if (!validUniversal) return;

          const validStore = this.validateSection(
            this.posSettingsForm,
            'DiscountSettings.EnableStoreDiscount',
            [
              { controlPath: 'DiscountSettings.MarketVendorId', label: 'Customer ID' },
              { controlPath: 'DiscountSettings.StoreDicountType', label: 'Discount Type' },
              { controlPath: 'DiscountSettings.StoreDiscountValue', label: 'Discount Value' },
            ],
            'Store Discount Settings',
            ['DiscountSettings']
          );
          if (!validStore) return;

          const validLayaway = this.validateSection(
            this.posSettingsForm,
            'SalesTypesSettings.AllowLayaway',
            [
              { controlPath: 'LayawaySettings.DownPaymentPercent', label: 'Down Payment' },
              { controlPath: 'LayawaySettings.LayawayNumberOfDays', label: 'Number of Days' },
              { controlPath: 'LayawaySettings.NumberOfInstalments', label: 'Number of Installments' },
            ],
            'Layaway Settings',
            ['LayawaySettings']
          );
          if (!validLayaway) return;

          if (this.posSettingsForm.valid) {
            const discountSettings = this.posSettingsForm.get('DiscountSettings')?.value;
            const marketVendorId = discountSettings?.MarketVendorId;
            const marketVendorIdForGC = this.posSettingsForm.get('OtherSettings.GcMarketVendorId')?.value;

            const retailCustomerId = this.posSettingsForm.get('SalesTypesSettings.MarketCustIdForRetail')?.value;
            const pendingSaleCustomerId = this.posSettingsForm.get('SalesTypesSettings.MarketCustIdForPendingSale')?.value;

            // Step 1: Retail Customer validation if entered
            const validateRetailCustomer$ = retailCustomerId
              ? this.posSalesService.checkMarketCustomerExists(retailCustomerId).pipe(
                map((result: any) => {
                  if (!result.exists) {
                    this.toastr.warning(
                      $localize`Retail Customer Id doesn't exist.`,
                      $localize`Validation`,
                      this.sharedHTMLSettings.ToasterConfig
                    );
                    throw new Error("Invalid Retail Customer Id");
                  }

                  this.posSettingsForm
                    .get('SalesTypesSettings.DefCustIdForRetail')
                    ?.patchValue(result.customerId, { emitEvent: false });

                  return true;
                })
              )
              : of(true);

            // Step 2: Pending Sale Customer validation if entered
            const validatePendingCustomer$ = pendingSaleCustomerId
              ? this.posSalesService.checkMarketCustomerExists(pendingSaleCustomerId).pipe(
                map((result: any) => {
                  if (!result.exists) {
                    this.toastr.warning(
                      $localize`Pending Sale Customer Id doesn't exist.`,
                      $localize`Validation`,
                      this.sharedHTMLSettings.ToasterConfig
                    );
                    throw new Error("Invalid Pending Sale Customer Id");
                  }

                  this.posSettingsForm
                    .get('SalesTypesSettings.DefCustIdForPendingSale')
                    ?.patchValue(result.customerId, { emitEvent: false });

                  return true;
                })
              )
              : of(true);

            // Step 3: GC Vendor validation if entered
            const validateGCVendor$ = marketVendorIdForGC
              ? this.vendorService.getVendorInfoByMarketVendorId(marketVendorIdForGC).pipe(
                map(vendor => {
                  if (!vendor || !vendor.VendorId) {
                    const fieldName = this.wordReplacePipe.transform('Vendor Id');
                    this.toastr.warning(
                      $localize`${fieldName} for Gift Certificate doesn't exist.`,
                      $localize`Validation`,
                      this.sharedHTMLSettings.ToasterConfig
                    );
                    throw new Error("Invalid Vendor Id");
                  }

                  this.posSettingsForm
                    .get('OtherSettings.GcVendorId')
                    ?.patchValue(vendor.VendorId, { emitEvent: false });

                  return true;
                })
              )
              : of(true);


            // Step 4: Booth number validation if entered.
            const boothNumber = this.posSettingsForm.get('OtherSettings.GcBoothNumber')?.value;

            const validateBooth$ = boothNumber
              ? this.layoutService.validateBoothNumber(boothNumber).pipe(
                map(isValid => {
                  if (!isValid) {
                    this.toastr.warning(
                      $localize`Booth Number for Gift Certificate doesn't exist.`,
                      $localize`Validation`,
                      this.sharedHTMLSettings.ToasterConfig
                    );
                    throw new Error("Invalid Booth Number");
                  }
                  return true;
                })
              )
              : of(true);

            // Step 5: Vendor validation if entered
            const validateStoreDiscVendor$ = marketVendorId
              ? this.vendorService.getVendorInfoByMarketVendorId(marketVendorId).pipe(
                map(vendor => {
                  if (!vendor || !vendor.VendorId) {
                    const fieldName = this.wordReplacePipe.transform('Vendor Id');
                    this.toastr.warning(
                      $localize`${fieldName} for Store Discount doesn't exist.`,
                      $localize`Validation`,
                      this.sharedHTMLSettings.ToasterConfig
                    );
                    throw new Error("Invalid Vendor Id");
                  }

                  this.posSettingsForm
                    .get('DiscountSettings.StoreDiscountVendorId')
                    ?.patchValue(vendor.VendorId, { emitEvent: false });

                  return true;
                })
              )
              : of(true);

            // Step 6: Chain validations → only save if all are valid
            forkJoin([validateRetailCustomer$, validatePendingCustomer$, validateGCVendor$, validateBooth$, validateStoreDiscVendor$])
              .pipe(takeUntil(this.destroy$))
              .subscribe({
                next: () => {
                  const payload = this.preparePayload();

                  if (!marketVendorId) {
                    if (payload.DiscountSettings) {
                      payload.DiscountSettings.StoreDiscountVendorId = null;
                    }
                  }

                  if (!marketVendorIdForGC) {
                    if (payload.OtherSettings) {
                      payload.OtherSettings.GcVendorId = null;
                    }
                  }

                  this.loaderIcon = Promise.resolve(true);
                  this.posSettingsService.savePosSettings(payload)
                    .pipe(takeUntil(this.destroy$))
                    .subscribe({
                      next: () => {
                        const useQboForCheckPrinting = this.posSettingsForm.get('OtherSettings.UseQboforCheckPrinting')?.value;
                        this.posSettingsService.setUseQboForCheckPrinting(!!useQboForCheckPrinting);
                        
                        const isPosByVendorChanged = this.posSettingsForm.get('OtherSettings.PosbyVendor')?.dirty;

                        this.originalPosSettings = { ...this.posSettingsForm.value };
                        this.posSettingsForm.markAsPristine();

                        if (isPosByVendorChanged) {
                          const taxPayload = this.taxSettingsForm.getRawValue() as SalesTaxAndCommissionModel;

                          this.posSettingsService.saveSalesTaxAndCommission(taxPayload)
                            .pipe(takeUntil(this.destroy$))
                            .subscribe({
                              next: () => {
                                this.originalTaxSettings = { ...this.taxSettingsForm.value };
                                this.taxSettingsForm.markAsPristine();

                                this.loaderIcon = Promise.resolve(false);
                                this.toastr.success(
                                  $localize`POS settings saved successfully.`,
                                  $localize`Success`,
                                  this.sharedHTMLSettings.ToasterConfig
                                );
                              },
                              error: err => {
                                this.loaderIcon = Promise.resolve(false);
                                this.toastr.error(
                                  $localize`Failed to save sales tax settings.`,
                                  $localize`Error`,
                                  this.sharedHTMLSettings.ToasterConfig
                                );
                              }
                            });

                        } else {
                          this.loaderIcon = Promise.resolve(false);
                          this.toastr.success(
                            $localize`POS settings saved successfully.`,
                            $localize`Success`,
                            this.sharedHTMLSettings.ToasterConfig
                          );
                        }
                      },
                      error: err => {
                        this.loaderIcon = Promise.resolve(false);
                        this.toastr.error(
                          $localize`Failed to save POS settings.`,
                          $localize`Error`,
                          this.sharedHTMLSettings.ToasterConfig
                        );
                      }
                    });
                },
                error: err => {
                  this.loaderIcon = Promise.resolve(false);
                  console.warn("Validation failed, not saving:", err);
                }
              });
          } else {
            this.posSettingsForm.markAllAsTouched();
          }
          break;

        case 'Pos_Sales_Tax_Settings':
          const validPosTax = this.validateSection(
            this.taxSettingsForm,
            'TaxSettings.ApplyPossalesTax',
            [{ controlPath: 'TaxSettings.PossalesTaxPercent', label: 'Sales Tax' },],
            'POS Tax Settings',
            ['TaxSettings']
          );
          if (!validPosTax) return;

          const validPosSecondLevelTax = this.validateSection(
            this.taxSettingsForm,
            'TaxSettings.ApplyPossecondLevelTax',
            [
              { controlPath: 'TaxSettings.PossecondLevelTaxPercent', label: 'Second level Tax' },
              { controlPath: 'TaxSettings.PossecondLevelTaxName', label: 'Second level Tax Name' },
            ],
            'POS Tax Settings',
            ['TaxSettings']
          );
          if (!validPosSecondLevelTax) return;

          const fieldName = this.wordReplacePipe.transform('Vendor');
          const validCommission = this.validateSection(
            this.taxSettingsForm,
            'Commission.ApplyCommission',
            [
              { controlPath: 'Commission.DealerConCommPercent', label: `${fieldName}wise Consignment Commission` },
            ],
            'Commissions',
            ['Commission']
          );
          if (!validCommission) return;

          const validDefaultItem = this.validateSection(
            this.taxSettingsForm,
            'POSSales.FillDefaultIteminPos',
            [
              { controlPath: 'POSSales.DefaultItemName', label: 'Default Item Description' },
            ],
            'POS Sales',
            ['POSSales']
          );
          if (!validDefaultItem) return;

          const validOtherCharge = this.validateSection(
            this.taxSettingsForm,
            'POSSales.EnableOtherChargeinPos',
            [
              { controlPath: 'POSSales.PosotherChargeName', label: 'POS Other Charge Name' },
            ],
            'POS Sales',
            ['POSSales']
          );

          if (!validOtherCharge) return;

          if (this.taxSettingsForm.valid) {

            const oldCommission = this.originalTaxSettings?.Commission;
            const newCommission = this.taxSettingsForm.get('Commission')?.getRawValue();

            const flatCommissionChanged = Number(oldCommission?.FlatCommission) !== Number(newCommission?.FlatCommission);
            const consignmentCommissionChanged = Number(oldCommission?.DealerConCommPercent) !== Number(newCommission?.DealerConCommPercent);

            if (flatCommissionChanged) {

              Swal.fire({
                title: 'POS Settings',
                text: 'Do you want to update the Flat Commission for POS Invoice Items created after the last vendor payout for vendors using the default POS Flat Commission?',
                icon: 'question',
                showCancelButton: true,
                confirmButtonText: 'Yes',
                cancelButtonText: 'No'
              }).then(result => {
                this.saveSalesTaxSettings(result.isConfirmed, flatCommissionChanged, consignmentCommissionChanged);
              });
            } else {
              this.saveSalesTaxSettings(false, flatCommissionChanged, consignmentCommissionChanged);
            }
          } else {
            this.taxSettingsForm.markAllAsTouched();
          }
          break;

        case 'Message_Settings':
          if (this.messageSettingsForm.valid) {
            const payload = this.messageSettingsForm.value as MessageSettingsModel;
            this.loaderIcon = Promise.resolve(true);
            this.posSettingsService.saveMessageSettings(payload)
              .pipe(takeUntil(this.destroy$))
              .subscribe({
                next: () => {
                  this.originalMessageSettings = { ...this.messageSettingsForm.value };
                  this.messageSettingsForm.markAsPristine();
                  this.loaderIcon = Promise.resolve(false);
                  this.toastr.success($localize`Message settings saved successfully.`, $localize`Success`, this.sharedHTMLSettings.ToasterConfig);
                },
                error: err => {
                  this.loaderIcon = Promise.resolve(false);
                  this.toastr.error($localize`Failed to save message settings.`, $localize`Error`, this.sharedHTMLSettings.ToasterConfig);
                }
              });
          } else {
            this.messageSettingsForm.markAllAsTouched();
          }
          break;

        default:
          console.warn("No active tab found. Save failed.");
          this.toastr.warning($localize`Please select a valid tab to save.`, $localize`Warning`, this.sharedHTMLSettings.ToasterConfig);
      }
    });
  }

  private saveSalesTaxSettings(
    updatePosInvoiceItems: boolean,
    flatCommissionChanged: boolean,
    consignmentCommissionChanged: boolean
  ): void {

    const payload = this.taxSettingsForm.getRawValue() as SalesTaxAndCommissionModel;

    this.loaderIcon = Promise.resolve(true);
    this.posSettingsService.saveSalesTaxAndCommission(payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {

          const isAllowPosSalesChanged = this.taxSettingsForm.get('POSSales.AllowPossalesWithOutBooking')?.dirty;

          this.originalTaxSettings = { ...this.taxSettingsForm.value };
          this.taxSettingsForm.markAsPristine();
          if (flatCommissionChanged || consignmentCommissionChanged) {

            this.updateDefaultVendorCommissions(
              updatePosInvoiceItems,
              flatCommissionChanged,
              consignmentCommissionChanged
            );

          }
          if (isAllowPosSalesChanged) {
            const posPayload = this.preparePayload();
            this.posSettingsService.savePosSettings(posPayload)
              .pipe(takeUntil(this.destroy$))
              .subscribe({
                next: () => {
                  this.originalPosSettings = { ...this.posSettingsForm.value };
                  this.posSettingsForm.markAsPristine();

                  this.loaderIcon = Promise.resolve(false);
                  this.toastr.success(
                    $localize`Sales tax settings saved successfully.`,
                    $localize`Success`,
                    this.sharedHTMLSettings.ToasterConfig
                  );
                },
                error: () => {
                  this.loaderIcon = Promise.resolve(false);
                  this.toastr.error(
                    $localize`Failed to save POS settings.`,
                    $localize`Error`,
                    this.sharedHTMLSettings.ToasterConfig
                  );
                }
              });
          } else {
            this.loaderIcon = Promise.resolve(false);
            this.toastr.success(
              $localize`Sales tax settings saved successfully.`,
              $localize`Success`,
              this.sharedHTMLSettings.ToasterConfig
            );
          }
        },
        error: () => {
          this.loaderIcon = Promise.resolve(false);
          this.toastr.error(
            $localize`Failed to save sales tax settings.`,
            $localize`Error`,
            this.sharedHTMLSettings.ToasterConfig
          );
        }
      });
  }

  private updateDefaultVendorCommissions(
    updatePosInvoiceItems: boolean,
    flatCommissionChanged: boolean,
    consignmentCommissionChanged: boolean
  ): void {

    const newCommission = this.taxSettingsForm.get('Commission')?.getRawValue();

    const payload = {
      FlatCommission: Number(newCommission.FlatCommission ?? 0),
      ConsignmentCommission: Number(newCommission.DealerConCommPercent ?? 0),
      FlatCommissionChanged: flatCommissionChanged,
      ConsignmentCommissionChanged: consignmentCommissionChanged,
      UpdatePosInvoiceItems: flatCommissionChanged && updatePosInvoiceItems
    };
    this.vendorService.UpdateDefaultVendorCommissions(payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.toastr.success(
            res?.Message ?? $localize`Vendor commissions updated successfully.`,
            $localize`Success`,
            this.sharedHTMLSettings.ToasterConfig
          );
        },
        error: () => {
          this.toastr.error(
            $localize`Failed to update vendor commissions.`,
            $localize`Error`,
            this.sharedHTMLSettings.ToasterConfig
          );
        }
      });
  }

  private setupConditionalValidators(
    form: FormGroup,
    enableControlPath: string,
    fieldConfigs: { controlPath: string; validators: any[] }[]
  ): void {
    const enableControl = form.get(enableControlPath);

    enableControl?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((isEnabled: boolean) => {
      fieldConfigs.forEach(cfg => {
        const ctrl = form.get(cfg.controlPath);
        if (!ctrl) return;

        if (isEnabled) {
          ctrl.setValidators(cfg.validators);
        } else {
          ctrl.clearValidators();
        }

        ctrl.updateValueAndValidity();
      });
    });
  }

  private validateSection(
    form: FormGroup,
    enableControlPath: string,
    requiredFields: { controlPath: string; label: string }[],
    sectionName: string,
    touchGroups?: string[]
  ): boolean {
    const enableControl = form.get(enableControlPath);

    if (!enableControl?.value) {
      return true;
    }

    const missingFields: string[] = [];
    const invalidFields: string[] = [];

    requiredFields.forEach(field => {
      const ctrl = form.get(field.controlPath);

      if (!ctrl) {
        return;
      }

      const value = ctrl.value;

      // Required field validation
      if (value === null || value === undefined || value === '') {
        missingFields.push(field.label);
        return;
      }

      // Other validation errors such as min/max
      if (ctrl.invalid) {
        if (ctrl.hasError('min')) {
          invalidFields.push(`${field.label} must be greater than zero`);
        } else if (ctrl.hasError('max')) {
          invalidFields.push(`${field.label} cannot be greater than 100`);
        } else {
          invalidFields.push(field.label);
        }
      }
    });

    if (missingFields.length > 0) {
      const truncated = missingFields.length > 3
        ? missingFields.slice(0, 3).join(', ') + '...'
        : missingFields.join(', ');

      this.toastr.warning(
        `Please fill mandatory fields in ${sectionName}: ${truncated}`,
        $localize`Validation`,
        this.sharedHTMLSettings.ToasterConfig
      );

      touchGroups?.forEach(groupPath => {
        form.get(groupPath)?.markAllAsTouched();
      });

      return false;
    }

    if (invalidFields.length > 0) {
      const truncated = invalidFields.length > 3
        ? invalidFields.slice(0, 3).join(', ') + '...'
        : invalidFields.join(', ');

      this.toastr.warning(
        `Please enter valid values in ${sectionName}: ${truncated}.`,
        $localize`Validation`,
        this.sharedHTMLSettings.ToasterConfig
      );

      touchGroups?.forEach(groupPath => {
        form.get(groupPath)?.markAllAsTouched();
      });

      return false;
    }

    return true;
  }

  cancelChanges() {
    const activeTabId = this.getActiveTabId();
    let form: FormGroup | null = null;
    let originalValues: any = null;

    switch (activeTabId) {
      case 'Pos_Settings':
        form = this.posSettingsForm;
        originalValues = this.originalPosSettings;
        break;

      case 'Pos_Sales_Tax_Settings':
        form = this.taxSettingsForm;
        originalValues = this.originalTaxSettings;
        break;

      case 'Message_Settings':
        form = this.messageSettingsForm;
        originalValues = this.originalMessageSettings;
        break;

      default:
        console.warn('No active tab found for cancel.');
        return;
    }

    const navigateToDashboard = () => {
      if (originalValues) {
        form.reset(originalValues);
        form.markAsPristine();
      }
      this.router.navigate(['/dashboard']);
    };

    if (form?.dirty) {
      Swal.fire({
        title: $localize`Confirm`,
        text: $localize`Unsaved changes will be lost. Are you sure you want to continue?`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: $localize`Yes`,
        cancelButtonText: $localize`No`,
      }).then(result => {
        if (result.isConfirmed) {
          navigateToDashboard();
        }
      });
    } else {
      navigateToDashboard();
    }
  }

  private setupPosByVendorValidation(): void {
    const posByVendorControl = this.posSettingsForm.get('OtherSettings.PosbyVendor');
    const allowPosWithoutBookingControl = this.taxSettingsForm.get('POSSales.AllowPossalesWithOutBooking');

    if (!posByVendorControl || !allowPosWithoutBookingControl) {
      return;
    }

    const updateState = (isPosByVendor: boolean) => {
      if (isPosByVendor) {
        allowPosWithoutBookingControl.enable({ emitEvent: false });
      } else {
        allowPosWithoutBookingControl.setValue(false, { emitEvent: false });
        allowPosWithoutBookingControl.disable({ emitEvent: false });
      }
    };

    updateState(!!posByVendorControl.value);

    posByVendorControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(value => {
      updateState(!!value);
      if (!value && posByVendorControl.dirty) {
        this.toastr.info($localize`POS Sales Without Booking is available only when POS Transaction by Vendor is enabled.`, $localize`Info`, this.sharedHTMLSettings.ToasterConfig);
      }
    });
  }

  getActiveTabId(): string {
    const activeTab = document.querySelector('.tab-pane.show.active');
    return activeTab ? activeTab.id : '';
  }

  trimAllTextFields(formGroup: FormGroup) {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      if (control instanceof FormGroup) {
        this.trimAllTextFields(control);
      } else {
        if (typeof control?.value === 'string') {
          control.setValue(control.value.trim());
        }
      }
    });
  }

  private updateSalesTypesForAllUsers(): void {
    const salesGroup = this.posSettingsForm.get('SalesTypesSettings') as FormGroup;

    const salesTypes = [
      {
        UstIntSaleTypeID: 1,
        UstBitSaleTypeAllowed: salesGroup.get('AllowRetail')?.value
      },
      {
        UstIntSaleTypeID: 2,
        UstBitSaleTypeAllowed: salesGroup.get('AllowWholeSale')?.value
      },
      {
        UstIntSaleTypeID: 3,
        UstBitSaleTypeAllowed: salesGroup.get('AllowShipped')?.value
      },
      {
        UstIntSaleTypeID: 5,
        UstBitSaleTypeAllowed: salesGroup.get('AllowLayaway')?.value
      },
      {
        UstIntSaleTypeID: 6,
        UstBitSaleTypeAllowed: salesGroup.get('AllowPending')?.value
      },
      {
        UstIntSaleTypeID: 7,
        UstBitSaleTypeAllowed: salesGroup.get('AllowReturn')?.value
      },
      {
        UstIntSaleTypeID: 8,
        UstBitSaleTypeAllowed: salesGroup.get('AllowVoid')?.value
      }
    ];

    this.userService.UpdateSalesTypeForAllUsers(salesTypes).pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          if (res?.Success) {
            // API succeeded → reset user permissions
            this.getPOSSettings()
          }
        },
        error: () => {
          this.toastr.error(
            $localize`Failed to update sales types for users.`,
            $localize`Error`,
            this.sharedHTMLSettings.ToasterConfig
          );
        }
      });
  }

  getPOSSettings() {

    this.posSettingsService.getPosSettings().subscribe({
      next: (data: POSSettingsModel) => {
        this.allowRetail = data.SalesTypesSettings?.AllowRetail ?? false;
        this.allowWholeSale = data.SalesTypesSettings?.AllowWholeSale ?? false;
        this.allowShipped = data.SalesTypesSettings?.AllowShipped ?? false;
        this.allowLayaway = data.SalesTypesSettings?.AllowLayaway ?? false;
        this.allowPending = data.SalesTypesSettings?.AllowPending ?? false;
        this.allowReturn = data.SalesTypesSettings?.AllowReturn ?? false;
        this.allowVoid = data.SalesTypesSettings?.AllowVoid ?? false;
      },
      error: err => { }
    });

  }

  onBeforeOpen(args: any): void {
    if (!args || !args.target || !args.target.dataset || !args.target.dataset.content) {
      args.cancel = true;
    }
  }

  handleToggle(): void {
    this.isCollapsed = !this.isCollapsed;
  }
}
